import type { ElementType } from 'react';
import { archetypeByKey } from '../pessoal/jobArchetypes';
import { IsoPerson } from './officePeople';
import { roomPropForArch } from './officeTheme';
import { mulberry32, seedFrom } from './rng';

export type FurnitureKind =
  | 'desk'
  | 'sewing'
  | 'cuttable'
  | 'machine'
  | 'counter'
  | 'stove'
  | 'shelf'
  | 'maca'
  | 'table'
  | 'plant'
  | 'cooler';

export interface Workstation {
  c: number;
  r: number;
  person: IsoPerson;
}
export interface Room {
  key: string;
  label: string;
  color: string;
  Icon: ElementType;
  c0: number;
  r0: number;
  w: number;
  h: number;
  count: number;
}
export interface PropPlaced {
  kind: FurnitureKind;
  c: number;
  r: number;
  color?: string;
}
export interface OfficeModel {
  cols: number;
  rows: number;
  rooms: Room[];
  stations: Workstation[];
  props: PropPlaced[];
  total: number;
  overflow: number;
}

const CAP = 60; // máx de avatares vivos (o resto vira "+N")

/**
 * Gera a planta: agrupa pessoas por arquétipo -> uma SALA por função; faz
 * shelf-packing das salas em bandas; preenche estações dentro de cada sala.
 * Pura e determinística (seed pela empresa).
 */
export function buildOfficeLayout(people: IsoPerson[], seed = 1): OfficeModel {
  const total = people.length;
  const visiveis = people.slice(0, CAP);
  const overflow = Math.max(0, total - visiveis.length);

  // 1) agrupa por arquétipo -> salas, maiores primeiro
  const byArch = new Map<string, IsoPerson[]>();
  visiveis.forEach(p => {
    const arr = byArch.get(p.archetypeKey) ?? [];
    arr.push(p);
    byArch.set(p.archetypeKey, arr);
  });
  const grupos = Array.from(byArch.entries()).sort((a, b) => b[1].length - a[1].length);

  // 2) shelf-packing — salas LARGAS e RASAS (menos fileiras = menos empilhamento)
  // com pitch entre fileiras, p/ os móveis aparecerem e abrir espaço de circulação.
  const ROW_PITCH = 1.25;
  const gridW = (n: number) => Math.max(1, Math.ceil(Math.sqrt(n * 2.4)));
  const biggestW = grupos.length ? gridW(grupos[0][1].length) : 1;
  const bandW = Math.max(20, biggestW + 9);
  const rooms: Room[] = [];
  const stations: Workstation[] = [];
  const props: PropPlaced[] = [];
  const rnd = mulberry32(seedFrom(seed, 7));
  let cursorC = 0;
  let bandR0 = 0;
  let bandH = 0;
  let maxC = 0;

  grupos.forEach(([key, pessoas]) => {
    const n = pessoas.length;
    const w = gridW(n);
    const h = Math.ceil(n / w);
    const th = (h - 1) * ROW_PITCH + 1; // altura da sala em tiles
    if (cursorC > 0 && cursorC + w > bandW) {
      bandR0 += bandH + 2;
      cursorC = 0;
      bandH = 0;
    }
    const c0 = cursorC;
    const r0 = bandR0;
    const arq = archetypeByKey(key);
    rooms.push({
      key,
      label: arq.label,
      color: arq.accent,
      Icon: arq.Icon,
      c0,
      r0,
      w,
      h: th,
      count: n,
    });
    pessoas.forEach((person, i) => {
      stations.push({
        c: c0 + (i % w),
        r: r0 + Math.floor(i / w) * ROW_PITCH,
        person,
      });
    });
    // planta na FRENTE da sala (no piso) — nunca acima/fora da borda de trás.
    if (rnd() > 0.45) props.push({ kind: 'plant', c: c0 + w - 0.5, r: r0 + th + 0.5 });
    // móvel de sala (ex.: têxtil → mesa de corte) na frente da sala
    const rp = roomPropForArch(key);
    if (rp)
      props.push({
        kind: rp,
        c: c0 + Math.max(0, w / 2 - 1.5),
        r: r0 + th + 0.9,
        color: arq.accent,
      });
    cursorC += w + 2;
    bandH = Math.max(bandH, th);
    maxC = Math.max(maxC, c0 + w);
  });

  // extent cobre estações E props (mesa de corte fica abaixo da sala)
  const maxR = Math.max(bandR0 + bandH, ...stations.map(s => s.r + 1), ...props.map(p => p.r + 1));
  return {
    cols: Math.max(1, maxC, ...props.map(p => p.c + 1)),
    rows: Math.ceil(maxR),
    rooms,
    stations,
    props,
    total,
    overflow,
  };
}
